import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AppStore, BucketData, PrincipalData } from '@app/models';
import { CALENDAR_SELECT } from '@app/models/Calendar';
import { DateProvider, StoreProvider, UserProvider } from '@app/providers';
import { CountingService } from '@app/services';
import { CountingCircleStateService } from '@app/services/counting-circle-state.service';
import moment from 'moment-timezone';
import { Observable, of, switchMap, tap, filter, map } from 'rxjs';

@Component({
    standalone: false,
    templateUrl: './counting.component.html',
    styleUrls: ['./counting.component.scss'],
})
export class CountingComponent implements OnInit {
    public loading = true;
    public $app = this.$store.listen('app');
    public app!: AppStore;
    public page = 'main';
    public repartitionTab: 'visits' | 'groups' = 'visits';
    public isToday!: boolean;
    public isDayOrWeek!: boolean;

    public currentFormat!: CALENDAR_SELECT;
    public currentRepartitionRange: any;

    public circleData$ = this.circleState.circleData$;

    private timezone = 'Europe/Paris';
    private lastActiveNavName?: string;
    private lastPrincipalData?: PrincipalData;

    constructor(
        public $store: StoreProvider,
        private userProvider: UserProvider,
        private router: Router,
        private dateProvider: DateProvider,
        private countingService: CountingService,
        private circleState: CountingCircleStateService
    ) {}

    ngOnInit(): void {
        this.init();
    }

    private init() {
        this.$store.refreshRoutingSubNav(false);
        this.$app.subscribe((app) => {
            this.$store.emitActionBar({ isFull: true });
            this.app = app;
            this.timezone = app.props.selectedLocalBusiness.timezone;
            this.currentFormat = app.props.dates.select;

            const activeName = app.props.detailNav?.active?.name;

            // Quand on revient sur l'onglet "main", on force le reload
            if (
                activeName === 'main' &&
                this.lastActiveNavName &&
                this.lastActiveNavName !== 'main'
            ) {
                this.$store.emitReloadAll(true);
            }

            // Changement d'onglet -> on remet le cercle en loading
            if (
                this.lastActiveNavName &&
                activeName !== this.lastActiveNavName
            ) {
                this.circleState.setLoading();
            }

            this.lastActiveNavName = activeName;
        });
    }

    /**
     * Calcule le range/format à partir des dates du store.
     * Nécessaire pour TOUS les onglets (currentRepartitionRange sert
     * à visit-repartition et groups), mais ne fait AUCUN appel HTTP.
     */
    private computeRange(app: AppStore): { range: any; rangeAdapt: any } {
        this.currentFormat = app.props!.dates!.select!;

        let range = {
            begin: app.props!.dates!.current!.start.asUnix,
            end: app.props!.dates!.current!.end!.asUnix,
            beginVs: app.props!.dates!.versus!.start.asUnix,
            endVs: app.props!.dates!.versus!.end!.asUnix,
        };

        let hour = {
            openingBegin: this.dateProvider.getOpeningTime(
                range.begin,
                app.props!.selectedLocalBusiness!.openingCalendar
            ),
            openingBeginVs: this.dateProvider.getOpeningTime(
                range.beginVs,
                app.props!.selectedLocalBusiness!.openingCalendar
            ),
            closingEnd: this.dateProvider.getClosingTime(
                range.end,
                app.props!.selectedLocalBusiness!.openingCalendar
            ),
            closingEndVs: this.dateProvider.getClosingTime(
                range.endVs,
                app.props!.selectedLocalBusiness!.openingCalendar
            ),
        };

        this.isToday = this.dateProvider.checkIfToday(
            app.props!.dates!.select!,
            app.props!.dates!.current!.start.asUnix
        );
        this.isDayOrWeek = this.dateProvider.checkTypeIfDayOrWeek(
            app.props!.dates!.type!
        );

        let rangeAdapt = { ...range };

        if (this.isToday) {
            rangeAdapt.begin = this.dateProvider.setHourInTimeStamp(
                rangeAdapt.begin,
                hour.openingBegin
            );
            range.begin = this.dateProvider.setHourInTimeStamp(
                range.begin,
                hour.openingBegin
            );
            rangeAdapt.beginVs = this.dateProvider.setHourInTimeStamp(
                rangeAdapt.beginVs,
                hour.openingBeginVs
            );
            range.beginVs = this.dateProvider.setHourInTimeStamp(
                range.beginVs,
                hour.openingBeginVs
            );
            rangeAdapt.end = this.dateProvider.setHourInTimeStampCustom(
                moment().tz(this.timezone).hour()
            );

            if (this.isDayOrWeek) {
                rangeAdapt.endVs = this.dateProvider.dayLessOneType(
                    moment(rangeAdapt.end / 1000).tz(this.timezone),
                    app.props!.dates!.type!
                );
                if (
                    app.props!.dates!.type === 'week' &&
                    app.props!.dates!.select === 'week'
                ) {
                    let tmp = moment(rangeAdapt.endVs / 1000).tz(this.timezone);
                    rangeAdapt.endVs = tmp.set('hour', 23).unix() * 1000000;
                }
            } else if (
                (app.props!.dates!.type === 'month' &&
                    app.props!.dates!.select === 'week') ||
                (app.props!.dates!.type === 'year' &&
                    app.props!.dates!.select === 'week')
            ) {
                rangeAdapt.endVs = range.endVs;
            } else if (
                app.props!.dates!.type === 'year' &&
                app.props!.dates!.select === 'month'
            ) {
                let tmp = moment(rangeAdapt.endVs / 1000).tz(this.timezone);
                rangeAdapt.endVs = tmp.set('hour', 23).unix() * 1000000;
            } else if (
                app.props!.dates!.type === 'month' &&
                app.props!.dates!.select === 'month'
            ) {
                let today = moment().tz(this.timezone).unix() * 1000000;
                let tmp = moment(today / 1000).tz(this.timezone);
                rangeAdapt.endVs =
                    tmp.subtract(1, 'month').set('hour', 23).unix() * 1000000;
            } else if (
                app.props!.dates!.type === 'year' &&
                app.props!.dates!.select === 'year'
            ) {
                let today = moment().tz(this.timezone).unix() * 1000000;
                let endOfMonth = moment(today / 1000)
                    .tz(this.timezone)
                    .endOf('month');
                rangeAdapt.endVs =
                    endOfMonth.clone().subtract(1, 'year').unix() * 1000000;
            } else {
                let endvs = range.endVs;
                rangeAdapt.endVs = this.dateProvider.setDayHourOfDay(
                    moment(endvs / 1000).tz(this.timezone),
                    moment(rangeAdapt.end / 1000).tz(this.timezone)
                );
            }

            this.currentRepartitionRange = range;
        } else {
            range.begin = this.dateProvider.setHourInTimeStamp(
                range.begin,
                hour.openingBegin
            );
            range.beginVs = this.dateProvider.setHourInTimeStamp(
                range.beginVs,
                hour.openingBeginVs
            );
            range.end = this.dateProvider.setHourInTimeStamp(
                range.end,
                hour.closingEnd
            );
            range.endVs = this.dateProvider.setHourInTimeStamp(
                range.endVs,
                hour.closingEndVs
            );

            this.currentRepartitionRange = range;
        }

        return { range, rangeAdapt };
    }

    public $bucketData: Observable<BucketData> = this.$app.pipe(
        tap((app) => (this.app = app)),
        filter(() => this.$store._getReloadAll()),

        // Le range est TOUJOURS recalculé (nécessaire à tous les onglets),
        // mais aucun appel HTTP n'est fait ici.
        map((app) => ({ app, ...this.computeRange(app) })),

        switchMap(({ app, range, rangeAdapt }) => {
            const activeName = app.props!.detailNav?.active?.name;

            if (activeName !== 'main') {
                this.loading = false;
                this.$store.emitReloadAll(false);
                return of(this.lastPrincipalData as PrincipalData);
            }

            this.loading = true;

            return this.isToday
                ? this.countingService.getPrincipalDataCustom(
                      app.props!.selectedLocalBusiness!.id!,
                      app.props!.dates!.select!,
                      rangeAdapt
                  )
                : this.countingService.getPrincipalData(
                      app.props!.selectedLocalBusiness!.id!,
                      app.props!.dates!.select!,
                      range
                  );
        }),

        map((principalData: PrincipalData) => {
            this.loading = false;
            this.$store.emitReloadAll(false);

            if (principalData) {
                this.lastPrincipalData = principalData;

                this.circleState.emit({
                    current: principalData.current.nbPerson,
                    versus: principalData.versus.nbPerson,
                    variation: principalData.comparison.nbPerson,
                    icon: '/assets/svg/customers-dark.svg',
                });
            }

            return { principalData } as never as BucketData;
        })
    );

    public setRepartitionTab(tab: 'visits' | 'groups'): void {
        this.repartitionTab = tab;
        this.circleState.setLoading();
        this.$store.emit({ app: { props: { detailNav: { subTab: tab } } } });
    }

    public disconect() {
        this.userProvider.updateUserdata({
            login: '',
            password: '',
            rememberOption: false,
            isLogged: false,
            isAllMainDataFetched: false,
            isFirstStart: true,
            disableAutoLogin: false,
            jwt: '',
        });
        this.router.navigate(['login'], { preserveFragment: false });
    }
}
